import DraggableMarker from 'components/BaseMap/components/DraggableMarker';
import L from 'leaflet';
import { useCallback, useMemo } from 'react';
import {
  getIpawsAreaResizeHandleCoordinates,
  getIpawsAreaResizeUpdates,
  IPAWS_AREA_TYPES,
} from '../../../../../../utils/ipawsArea.utils';
import AlertPointMarker from '../../../AlertPointMarker/AlertPointMarker';

// The anchor is the icon's center, so the visible circle sits directly on its map coordinate.
const resizeHandleIcon = L.divIcon({
  className: 'alert-creator-resize-handle-icon',
  html: '<span class="alert-creator-resize-handle" aria-hidden="true"></span>',
  iconSize: [28, 28],
  iconAnchor: [14, 14],
});

// This order matches the square corners produced by the shared shape helper.
const squareCornerNames = ['northeast', 'southeast', 'southwest', 'northwest'];

/**
 * Renders the center and resize handles for one selected square or circle. Drag callbacks first show
 * a temporary map preview, then save one final update when the user releases the handle.
 *
 * @param {Object} props
 * @param {Object} props.shape Last saved shape used for distance calculations.
 * @param {Object} [props.displayShape] Temporary shape shown during a live drag.
 * @param {string} props.shapeLabel Human-readable shape name used by accessible labels.
 * @param {Function} [props.onPreviewShape] Shows a temporary center or size change.
 * @param {Function} props.onUpdateShape Saves a final center or size change.
 * @returns {JSX.Element|null}
 */
const IpawsShapeEditHandles = ({
  shape,
  displayShape = shape,
  shapeLabel,
  onPreviewShape,
  onUpdateShape,
}) => {
  const isCircle = shape?.type === IPAWS_AREA_TYPES.CIRCLE;

  // Rebuild handles from the temporary shape so every handle follows the live resize preview.
  const resizeHandleCoordinates = useMemo(
    () => getIpawsAreaResizeHandleCoordinates(displayShape),
    [displayShape],
  );

  const getConstrainedResizeHandlePosition = useCallback(
    (handleIndex, handlePosition) => {
      if (shape?.type !== IPAWS_AREA_TYPES.SQUARE) return handlePosition;

      // First turn the pointer's distance from the fixed center into a new square size.
      const updates = getIpawsAreaResizeUpdates(shape, handlePosition);
      if (!updates) return handlePosition;

      // Then rebuild the square and return this handle's matching corner. This stops a handle from
      // drifting away from the square when the pointer moves at an angle.
      return (
        getIpawsAreaResizeHandleCoordinates({ ...shape, ...updates })[handleIndex] ?? handlePosition
      );
    },
    [shape],
  );

  if (!shape?.center || typeof onUpdateShape !== 'function') return null;

  return (
    <>
      <AlertPointMarker
        position={shape.center}
        markerIndex={0}
        accessibleName={`Move ${shapeLabel} center`}
        keyboard={false}
        showWorkplaceJurisdictionWarning={false}
        onMarkerMovePreview={(_index, center) => onPreviewShape?.({ center })}
        onMarkerMove={(_index, center) => onUpdateShape({ center })}
      />

      {resizeHandleCoordinates.map((position, handleIndex) => {
        const handleName = isCircle ? 'radius' : `${squareCornerNames[handleIndex]} corner`;

        return (
          <DraggableMarker
            // Leaflet marker labels are fixed at creation, including these resize handles.
            key={`${shape.id}-${shapeLabel}-${handleName}`}
            position={position}
            markerIndex={handleIndex}
            icon={resizeHandleIcon}
            accessibleName={`Resize ${shapeLabel} from its ${handleName}`}
            keyboard={false}
            zIndexOffset={1000}
            getConstrainedMarkerPosition={isCircle ? undefined : getConstrainedResizeHandlePosition}
            onMarkerDrag={(_index, newPosition) => {
              // Preview updates keep dragging smooth without saving every pointer movement.
              const updates = getIpawsAreaResizeUpdates(shape, newPosition);
              if (updates) onPreviewShape?.(updates);
            }}
            onMarkerDragEnd={(_index, newPosition) => {
              // Save once on release so the draft has one clear final value.
              const updates = getIpawsAreaResizeUpdates(shape, newPosition);
              if (updates) onUpdateShape(updates);
            }}
          />
        );
      })}
    </>
  );
};

export default IpawsShapeEditHandles;
